import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionSelectionChange } from '@angular/material/core';

export interface DataTableMultiSelectFilterOption {
  value: string;
  label?: string;
}

export interface DataTableMultiSelectFilterGroup {
  label: string;
  values: readonly string[];
}

@Component({
  selector: 'app-data-table-multi-select-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataTableMultiSelectFilterComponent),
      multi: true,
    },
  ],
  templateUrl: './data-table-multi-select-filter.component.html',
  styleUrl: './data-table-multi-select-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableMultiSelectFilterComponent implements ControlValueAccessor, OnChanges {
  @Input({ required: true }) label = '';
  @Input() options: readonly DataTableMultiSelectFilterOption[] = [];
  @Input() groups: readonly DataTableMultiSelectFilterGroup[] = [];
  @Input() panelClass: string | string[] = [];

  selectedValues: string[] = [];
  disabled = false;
  resolvedPanelClass = ['data-table-multi-select-filter-panel'];

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly expandedGroupLabels = new Set<string>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get selectionLabel(): string {
    if (this.selectedValues.length === 0) {
      return 'Ninguno';
    }

    if (this.areAllSelected) {
      return 'Todos';
    }

    return this.selectedValues
      .map(value => this.options.find(option => option.value === value)?.label ?? value)
      .join(', ');
  }

  get areAllSelected(): boolean {
    return this.options.length > 0 && this.options.every(option => this.selectedValues.includes(option.value));
  }

  get hasSelectedOptions(): boolean {
    return this.selectedValues.length > 0;
  }

  isGroupSelected(group: DataTableMultiSelectFilterGroup): boolean {
    return group.values.every(value => this.selectedValues.includes(value));
  }

  isGroupIndeterminate(group: DataTableMultiSelectFilterGroup): boolean {
    return !this.isGroupSelected(group) && group.values.some(value => this.selectedValues.includes(value));
  }

  isGroupExpandable(group: DataTableMultiSelectFilterGroup): boolean {
    return group.values.length > 1;
  }

  isGroupExpanded(group: DataTableMultiSelectFilterGroup): boolean {
    return this.expandedGroupLabels.has(group.label);
  }

  toggleGroupExpanded(group: DataTableMultiSelectFilterGroup): void {
    if (this.isGroupExpanded(group)) {
      this.expandedGroupLabels.delete(group.label);
    } else {
      this.expandedGroupLabels.add(group.label);
    }
    this.cdr.markForCheck();
  }

  toggleGroup(group: DataTableMultiSelectFilterGroup, checked: boolean): void {
    const groupValues = new Set(group.values);
    const selectedValues = this.selectedValues.filter(value => !groupValues.has(value));
    this.updateValue(checked ? [...selectedValues, ...group.values] : selectedValues);
  }

  getOption(value: string): DataTableMultiSelectFilterOption | undefined {
    return this.options.find(option => option.value === value);
  }

  trackByGroupLabel(_index: number, group: DataTableMultiSelectFilterGroup): string {
    return group.label;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['panelClass']) {
      const panelClasses = Array.isArray(this.panelClass) ? this.panelClass : [this.panelClass];
      this.resolvedPanelClass = ['data-table-multi-select-filter-panel', ...panelClasses];
    }
  }

  writeValue(value: unknown): void {
    this.selectedValues = this.normalizeValue(value);
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: string[]) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onOptionSelectionChange(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }

    this.toggleOption(event.source.value, event.source.selected);
  }

  toggleAll(checked: boolean): void {
    this.updateValue(checked ? this.options.map(option => option.value) : []);
  }

  markAsTouched(): void {
    this.onTouched();
  }

  trackByOptionValue(_index: number, option: DataTableMultiSelectFilterOption): string {
    return option.value;
  }

  private updateValue(value: unknown): void {
    this.selectedValues = this.normalizeValue(value);
    this.onChange(this.selectedValues);
    this.onTouched();
    this.cdr.markForCheck();
  }

  private toggleOption(value: string, checked: boolean): void {
    const selectedValues = this.selectedValues.filter(selectedValue => selectedValue !== value);
    this.updateValue(checked ? [...selectedValues, value] : selectedValues);
  }

  private normalizeValue(value: unknown): string[] {
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  }
}
